import i18n from '@/utils/i18n';
import React, { useCallback, useMemo } from 'react';
import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import {
  AlertFeed,
  AlertFeedProps,
} from '@/experiences/sales-portal/domain/insights/components/alert-feed';
import { AlertFeedEmpty } from '@/experiences/sales-portal/domain/insights/components/alert-feed-empty';
import { AlertFeedSkeleton } from '@/experiences/sales-portal/domain/insights/components/alert-feed-skeleton';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { toAlertCategories } from '@/experiences/sales-portal/domain/insights/blocs/transformers/alert-transformer';
import { useAlertStatusMutator } from '@/experiences/sales-portal/domain/insights/blocs/mutators/use-alert-status-mutator';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';

interface ActiveAlertsContainerProps {
  alerts: Alert[];
  error: Error | null;
  loading: boolean;
  refetch: () => void;
  renderFeed?: (props: AlertFeedProps) => React.ReactNode;
  renderSkeleton?: () => React.ReactNode;
  supplierUUID: string;
}

/**
 * Renders the active alerts feed from the loaded data passed in by the parent:
 * shows a loading skeleton, an error state with retry, an empty state, or the
 * list of alert cards. Hide and delete are optimistic (see
 * {@link useAlertStatusMutator}); failures surface a toast.
 */
export const ActiveAlertsContainer: React.FC<ActiveAlertsContainerProps> = ({
  alerts,
  error,
  loading,
  refetch,
  renderFeed,
  renderSkeleton,
  supplierUUID,
}) => {
  const { showErrorToast } = useExperienceToasts();

  const { visibleAlerts, hideAlert, deleteAlert } =
    useAlertStatusMutator(alerts);

  const categories = useMemo(
    () => toAlertCategories(visibleAlerts),
    [visibleAlerts],
  );

  const handleHide = useCallback(
    (alert: Alert) => {
      void hideAlert(alert).catch(() => showErrorToast());
    },
    [hideAlert, showErrorToast],
  );

  const handleDelete = useCallback(
    (alert: Alert) => {
      void deleteAlert(alert).catch(() => showErrorToast());
    },
    [deleteAlert, showErrorToast],
  );

  if (loading) {
    return <>{renderSkeleton ? renderSkeleton() : <AlertFeedSkeleton />}</>;
  }

  if (error) {
    return (
      <ErrorState
        description={i18n.t('sales_portal.error.description')}
        onPress={refetch}
        title={i18n.t('sales_portal.error.title')}
      />
    );
  }

  if (visibleAlerts.length === 0) {
    return <AlertFeedEmpty />;
  }

  const feedProps: AlertFeedProps = {
    categories,
    onDelete: handleDelete,
    onHide: handleHide,
    supplierUUID,
  };

  return (
    <>{renderFeed ? renderFeed(feedProps) : <AlertFeed {...feedProps} />}</>
  );
};
